import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { formatDate, statusColor } from "@/lib/utils";
import { toast } from "sonner";
import { Truck, UserCheck } from "lucide-react";

function TripDispatchCard({ tr, vehicles, drivers, onDone, t }) {
  const [veh, setVeh] = useState(tr.vehicle_id || "");
  const [drv, setDrv] = useState(tr.driver_id || "");
  const key = `${tr.code}-${tr.trip_index}`;
  const assign = async () => {
    if (!veh || !drv) return toast.error(t("pick_vehicle_driver"));
    try { await api.patch(`/bookings/${tr.booking_id}/trips/${tr.trip_index}/assign`, { vehicle_id: veh, driver_id: drv }); toast.success(t("assigned")); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("failed")); }
  };
  const fit = vehicles.filter((v) => v.type === tr.vehicle_type);
  return (
    <Card data-testid={`dispatch-${key}`}>
      <CardContent className="p-4 grid md:grid-cols-4 gap-3 items-end">
        <div>
          <div className="font-mono font-bold">{tr.code} <span className="text-slate-400 font-normal">#{tr.trip_index + 1}/{tr.trip_count}</span></div>
          <div className="text-sm font-medium">{tr.route}</div>
          <div className="text-xs text-slate-500">{formatDate(tr.pickup_date)} · {tr.vehicle_type} · {tr.passengers} pax</div>
          <div className="text-xs text-slate-500">{tr.customer_name} · {tr.customer_phone}</div>
          <div className="flex gap-1 mt-1"><Badge className={statusColor(tr.trip_status)}>{t(`st_${tr.trip_status}`)}</Badge><Badge className={statusColor(tr.payment_status)}>{t(`pay_${tr.payment_status}`)}</Badge></div>
        </div>
        <div><Label>{t("vehicle_type")}</Label>
          <Select value={veh} onValueChange={setVeh}>
            <SelectTrigger data-testid={`sel-veh-${key}`}><SelectValue placeholder={t("select")} /></SelectTrigger>
            <SelectContent className="bg-white">{(fit.length ? fit : vehicles).map((v) => <SelectItem key={v.id} value={v.id}>{v.plate} · {v.model}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>{t("driver")}</Label>
          <Select value={drv} onValueChange={setDrv}>
            <SelectTrigger data-testid={`sel-drv-${key}`}><SelectValue placeholder={t("select")} /></SelectTrigger>
            <SelectContent className="bg-white">{drivers.map((d) => <SelectItem key={d.id} value={d.user_id}>{d.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div>
          {tr.driver_name && <div className="text-xs text-emerald-700 mb-1 flex items-center gap-1" data-testid={`assigned-${key}`}><UserCheck className="w-3 h-3" />{tr.driver_name} · {tr.vehicle_plate}</div>}
          <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={assign} data-testid={`assign-${key}`}><Truck className="w-4 h-4 mr-1" />{tr.driver_id ? t("reassign") : t("assign")}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function AdminDispatch() {
  const { t } = useApp();
  const [trips, setTrips] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [tab, setTab] = useState("unassigned");
  const load = () => api.get("/dispatch/trips").then((r) => setTrips(r.data.items));
  useEffect(() => { load(); api.get("/vehicles").then((r) => setVehicles(r.data.items)); api.get("/drivers").then((r) => setDrivers(r.data.items)); }, []);
  const shown = trips.filter((tr) => (tab === "unassigned" ? !tr.driver_id : !!tr.driver_id));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-bold">{t("dispatch")}</h1>
        <div className="ml-auto inline-flex rounded-full bg-slate-100 p-1">
          {[["unassigned", t("unassigned")], ["assigned", t("assigned_tab")]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`dispatch-tab-${k}`} className={`px-4 py-1.5 text-sm rounded-full ${tab === k ? "bg-emerald-600 text-white" : "text-slate-600"}`}>{l} ({trips.filter((tr) => (k === "unassigned" ? !tr.driver_id : !!tr.driver_id)).length})</button>))}
        </div>
      </div>
      <div className="grid gap-3">
        {shown.length === 0 && <Card><CardContent className="p-8 text-center text-slate-500">{t("nothing_to_dispatch")}</CardContent></Card>}
        {shown.map((tr) => <TripDispatchCard key={`${tr.booking_id}-${tr.trip_index}`} tr={tr} vehicles={vehicles} drivers={drivers} onDone={load} t={t} />)}
      </div>
    </div>
  );
}
